import React from 'react';
import OrganizationDropdown from 'components/OrganizationDropdown';
import SubmittingSection from 'components/SubmissionSection';
import { isRequired } from 'forms/validators';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Form, Grid } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import ErrorAlert from 'components/ErrorAlert';
import { NEW_ORGANIZATION_NAMESPACE } from 'services/organizations/constants';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';
import { requestedNewOrganization } from 'services/organizations/actions';
import { AppState } from 'services/types';
import { NewOrganizationPayload } from 'services/organizations/types';
import OrganizationNameInput from './components/OrganizationNameInput';
import AreaStructureDropdown from './components/AreaStructureDropdown';
import ModuleCheckbox from './components/ModuleCheckbox';

interface Props {
    onClose: () => void;
}

export default function NewOrganizationForm(props: Props): React.ReactElement {
    const { onClose } = props;

    const dispatch = useDispatch();
    const currentOrganizationId = useSelector((state: AppState) =>
        selectCurrentOrganizationId(state, {})
    );

    const onSubmit = (values: NewOrganizationPayload) => {
        dispatch(requestedNewOrganization(values));
    };

    const renderForm = (formProps: FormRenderProps<NewOrganizationPayload>) => (
        <Form onSubmit={formProps.handleSubmit}>
            <Grid>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <OrganizationNameInput
                            name="newOrganizationName"
                            label="New organization name"
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <OrganizationDropdown
                            name="sourceOrganizationId"
                            label="Import existing organization data"
                            validate={isRequired}
                            disabled
                            clearable={false}
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <h5>Required Modules</h5>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={4}>
                        <ModuleCheckbox
                            name="includeJobs"
                            label="Jobs"
                            disabled
                        />
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <ModuleCheckbox
                            name="includeArea"
                            label="Area"
                            disabled
                        />
                    </Grid.Column>
                    <Grid.Column width={8}>
                        <ModuleCheckbox
                            name="includeWorkforce"
                            label="Workforce"
                            disabled
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <h5>Additional Modules</h5>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={4}>
                        <ModuleCheckbox name="includeHarvest" label="Harvest" />
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <ModuleCheckbox
                            name="includeTimesheets"
                            label="Timesheets"
                        />
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <ModuleCheckbox name="includeSpray" label="Spray" />
                    </Grid.Column>
                    <Grid.Column width={4}>
                        <ModuleCheckbox
                            name="includeFertilizer"
                            label="Fertilizer"
                        />
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <AreaStructureDropdown
                            name="organizationType"
                            label="Area module structure"
                            validate={isRequired}
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <ErrorAlert namespace={NEW_ORGANIZATION_NAMESPACE} />
            <SubmittingSection
                submittingButtonParam={{
                    namespace: NEW_ORGANIZATION_NAMESPACE
                }}
                normalCancelButtonParam={{
                    onClose: (): void => onClose()
                }}
            />
        </Form>
    );

    return (
        <FinalForm
            onSubmit={onSubmit}
            initialValues={{
                sourceOrganizationId: currentOrganizationId,
                includeJobs: true,
                includeArea: true,
                includeWorkforce: true
            }}
            render={(formProps) => renderForm(formProps)}
        />
    );
}
